import type { UserType } from '../types';

const BUSINESS_KEYWORDS = [
  'business',
  'company',
  'startup',
  'revenue',
  'expenses',
  'employees',
  'payroll',
  'inventory',
  'profit',
  'loss',
  'cash flow',
  'b2b',
  'clients',
  'customers',
  'vendor',
  'supplier',
  'invoice',
  'accounts receivable',
  'accounts payable',
  'operating costs',
  'overhead',
  'franchise',
  'llc',
  'corporation',
  'partnership',
  'sole proprietor',
  'entrepreneur',
  'self-employed',
  'freelance',
  'contractor',
];

const INDIVIDUAL_KEYWORDS = [
  'salary',
  'paycheck',
  'rent',
  'mortgage',
  'groceries',
  'bills',
  'student loan',
  'car payment',
  'credit card',
  'retirement',
  '401k',
  'ira',
  'savings',
  'emergency fund',
  'vacation',
  'kids',
  'family',
  'household',
  'personal',
  'spouse',
  'partner',
  'married',
  'single',
  'job',
  'career',
];

export function detectUserType(text: string): UserType | null {
  const lowerText = text.toLowerCase();

  let businessScore = 0;
  let individualScore = 0;

  for (const keyword of BUSINESS_KEYWORDS) {
    if (lowerText.includes(keyword)) {
      businessScore++;
    }
  }

  for (const keyword of INDIVIDUAL_KEYWORDS) {
    if (lowerText.includes(keyword)) {
      individualScore++;
    }
  }

  if (businessScore > individualScore && businessScore >= 2) {
    return 'BUSINESS';
  }

  if (individualScore > businessScore && individualScore >= 1) {
    return 'INDIVIDUAL';
  }

  // Default to individual if no strong signal
  if (text.length > 30) {
    return 'INDIVIDUAL';
  }

  return null;
}

export function extractIncomeRange(text: string): string | null {
  const lowerText = text.toLowerCase();

  // Look for explicit income mentions
  const incomePatterns = [
    { pattern: /(?:make|earn|income|salary).*?(\d{2,3})k/i, getValue: (match: RegExpMatchArray) => parseInt(match[1]) * 1000 },
    { pattern: /\$(\d{2,3}),?000/i, getValue: (match: RegExpMatchArray) => parseInt(match[1]) * 1000 },
    { pattern: /(\d{2,3})\s*thousand/i, getValue: (match: RegExpMatchArray) => parseInt(match[1]) * 1000 },
  ];

  for (const { pattern, getValue } of incomePatterns) {
    const match = text.match(pattern);
    if (match) {
      const value = getValue(match);
      if (value < 50000) return 'under50k';
      if (value < 100000) return '50k-100k';
      if (value < 200000) return '100k-200k';
      return 'over200k';
    }
  }

  // Look for qualitative mentions
  if (lowerText.includes('high income') || lowerText.includes('high earner')) {
    return 'over200k';
  }

  if (lowerText.includes('middle class') || lowerText.includes('average salary')) {
    return '50k-100k';
  }

  return null;
}

export function extractGoals(text: string): string[] {
  const lowerText = text.toLowerCase();
  const goals: string[] = [];

  const goalMappings = [
    { keywords: ['save', 'savings', 'emergency fund', 'rainy day'], goal: 'save_emergency' },
    { keywords: ['invest', 'investing', 'stocks', 'portfolio'], goal: 'invest' },
    { keywords: ['debt', 'pay off', 'credit card', 'loan'], goal: 'pay_debt' },
    { keywords: ['retire', 'retirement', '401k', 'pension'], goal: 'retirement' },
    { keywords: ['budget', 'track', 'expenses', 'spending'], goal: 'budget' },
    { keywords: ['house', 'home', 'mortgage', 'down payment'], goal: 'buy_home' },
    { keywords: ['vacation', 'travel', 'trip'], goal: 'vacation' },
    { keywords: ['automate', 'automatic', 'automatic transfer'], goal: 'automate' },
  ];

  for (const { keywords, goal } of goalMappings) {
    if (keywords.some((kw) => lowerText.includes(kw))) {
      goals.push(goal);
    }
  }

  return goals;
}
